import {
  LogLevel as LivekitLogLevel,
  setLogLevel as setLivekitLogLevel,
} from "@dtelecom/livekit-client"

export enum LogLevel {
  INFO = "INFO",
  WARN = "WARN",
  ERROR = "ERROR",
}

interface LogMessage {
  level: LogLevel
  timestamp: Date
  message: string
  data?: unknown[]
}

class Logger {
  private logLevel: LogLevel
  private isDevelopment: boolean

  constructor() {
    this.isDevelopment = import.meta.env.MODE === "development"
    this.logLevel = this.getLogLevelFromEnv()
    setLivekitLogLevel(this.getLivekitLogLevel())
  }

  log(message: string, ...data: unknown[]): void {
    this.write(LogLevel.INFO, message, ...data)
  }

  warn(message: string, ...data: unknown[]): void {
    this.write(LogLevel.WARN, message, ...data)
  }

  error(message: string, ...data: unknown[]): void {
    if (message.includes("Request failed with status code 418")) return

    const hasTeapotError = data.some((item) => {
      if (typeof item === "string") {
        return item.includes("Request failed with status code 418")
      }
      if (item && typeof item === "object" && "message" in item) {
        return String(item.message).includes(
          "Request failed with status code 418",
        )
      }
      return false
    })
    if (hasTeapotError) return

    this.write(LogLevel.ERROR, message, ...data)
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level
    setLivekitLogLevel(this.getLivekitLogLevel())
  }

  getLogLevel(): LogLevel {
    return this.logLevel
  }

  getLivekitLogLevel = (): LivekitLogLevel => {
    switch (this.logLevel) {
      case LogLevel.INFO:
        return LivekitLogLevel.debug
      case LogLevel.WARN:
        return LivekitLogLevel.warn
      case LogLevel.ERROR:
        return LivekitLogLevel.error
      default:
        return LivekitLogLevel.error
    }
  }

  private getLogLevelFromEnv(): LogLevel {
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL as string
    if (
      envLogLevel &&
      Object.values(LogLevel).includes(envLogLevel as LogLevel)
    ) {
      return envLogLevel as LogLevel
    }
    return this.isDevelopment ? LogLevel.INFO : LogLevel.ERROR
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR]
    return levels.indexOf(level) >= levels.indexOf(this.logLevel)
  }

  private formatMessage({ timestamp, level, message }: LogMessage): string {
    return `[${timestamp.toISOString()}] [${level}] ${message}`
  }

  private logToConsole(logMessage: LogMessage): void {
    const formatted = this.formatMessage(logMessage)
    const data = logMessage.data || []
    switch (logMessage.level) {
      case LogLevel.INFO:
        console.log(formatted, ...data)
        break
      case LogLevel.WARN:
        console.warn(formatted, ...data)
        break
      case LogLevel.ERROR:
        console.error(formatted, ...data)
        break
    }
  }

  // private sendToRemoteLogger(logMessage: LogMessage): void {
  //   console.info("Would send to remote logger:", logMessage)
  // }

  private write(level: LogLevel, message: string, ...data: unknown[]): void {
    if (!this.shouldLog(level)) return
    const logMessage: LogMessage = {
      level,
      timestamp: new Date(),
      message,
      data: data.length > 0 ? data : undefined,
    }
    this.logToConsole(logMessage)
    if (!this.isDevelopment && level === LogLevel.ERROR) {
      // add remote logger
    }
  }
}

export const logger = new Logger()
